import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
} from '@mui/material';
import {
  AccountTree as TreeIcon,
  People as PeopleIcon,
  Add as AddIcon,
  PhotoLibrary as PhotoIcon,
  Feedback as FeedbackIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

/**
 * QuickLinksCard Component
 *
 * Quick navigation links for easy access to common features
 *
 * Features:
 * - Navigate to family tree
 * - Browse people
 * - Add new person
 * - Upload photos
 * - Submit feedback
 * - Account settings
 *
 * @component
 * @returns {JSX.Element} Rendered quick links card
 */
export const QuickLinksCard: React.FC = () => {
  const navigate = useNavigate();

  const links = [
    {
      icon: <TreeIcon />,
      label: 'View Family Tree',
      description: 'Explore family connections',
      path: '/tree',
    },
    {
      icon: <PeopleIcon />,
      label: 'Browse People',
      description: 'Search family members',
      path: '/people',
    },
    {
      icon: <AddIcon />,
      label: 'Add Person',
      description: 'Add a family member',
      path: '/people/add',
    },
    {
      icon: <PhotoIcon />,
      label: 'Upload Photos',
      description: 'Share memories',
      path: '/people',
    },
    {
      icon: <FeedbackIcon />,
      label: 'Submit Feedback',
      description: 'Report issues or suggest features',
      path: '/feedback/submit',
    },
    {
      icon: <SettingsIcon />,
      label: 'Account Settings',
      description: 'Manage your profile',
      path: '/settings',
    },
  ];

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader title="Quick Links" />
      <CardContent sx={{ pt: 0, maxHeight: 500, overflow: 'auto' }}>
        <List disablePadding>
          {links.map((link, index) => (
            <ListItem key={index} disablePadding>
              <ListItemButton
                onClick={() => navigate(link.path)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'primary.main' }}>
                  {link.icon}
                </ListItemIcon>
                <ListItemText
                  primary={link.label}
                  secondary={link.description}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: 'medium',
                  }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};
